import { Setting } from './settings.service';

/**
 * VIRTUAL setting name. This is a UI-only grouping that lets an operator pick an
 * HD video profile in one click. It is NEVER sent to a device — submitting a
 * setting with this name would fail the backend's SettingType lookup
 * (InvalidSettingTypeException) because no firmware/module recognises it.
 * It always expands into the four REAL, registered settings below.
 */
export const VIDEO_PROFILE_SETTING_NAME = 'HD Video Profile - SF400';

/**
 * The four real, device-registered SettingType names that actually reach the
 * camera. These must exist as SettingTypes in devicesettings-api.
 */
export const VIDEO_PROFILE_TARGET_SETTINGS = {
  encoderExterior: 'Video Encoder Control - SF400 - Exterior',
  encoderInterior: 'Video Encoder Control - SF400 - Interior',
  sourceExterior: 'Video Source Control - SF400 - Exterior',
  sourceInterior: 'Video Source Control - SF400 - Interior',
} as const;

export interface VideoProfileValues {
  encoderExterior: string;
  encoderInterior: string;
  sourceExterior: string;
  sourceInterior: string;
}

export interface VideoProfile extends VideoProfileValues {
  id: string;
  label: string;
  description: string;
}

export const CUSTOM_PROFILE_ID = 'custom';

const RES = { '360p': 0, '720p': 1, '1080p': 2 } as const;
type Resolution = (typeof RES)[keyof typeof RES];

function encoderStr(res: Resolution, fps: 10 | 20, bitrate?: number): string {
  const base = `ENCODER=0,RES=${res},FPS=${fps}`;
  return bitrate ? `${base},BITRATE=${bitrate}` : base;
}

function sourceStr(res: Resolution, fps: 10 | 20): string {
  return `RES=${res},FPS=${fps}`;
}

export const VIDEO_PROFILES: VideoProfile[] = [
  {
    id: 'best_quality',
    label: 'Best Quality',
    description: '1080p road / 720p cab — highest video quality',
    encoderExterior: encoderStr(RES['1080p'], 20, 5200),
    encoderInterior: encoderStr(RES['720p'],  10, 2000),
    sourceExterior:  sourceStr(RES['1080p'], 20),
    sourceInterior:  sourceStr(RES['720p'],  20),
  },
  {
    id: 'better_quality',
    label: 'Better Quality',
    description: '720p road / 720p cab — HD on both cameras',
    encoderExterior: encoderStr(RES['720p'],  10, 3000),
    encoderInterior: encoderStr(RES['720p'],  10, 2000),
    sourceExterior:  sourceStr(RES['720p'],  20),
    sourceInterior:  sourceStr(RES['720p'],  20),
  },
  {
    id: 'better_storage',
    label: 'Better Storage',
    description: '720p road / 360p cab — HD road + more storage hours',
    encoderExterior: encoderStr(RES['720p'],  10, 3000),
    encoderInterior: encoderStr(RES['360p'],  10),
    sourceExterior:  sourceStr(RES['720p'],  20),
    sourceInterior:  sourceStr(RES['720p'],  20),
  },
  {
    id: 'best_storage',
    label: 'Best Storage',
    description: '360p road / 360p cab — maximum storage hours',
    encoderExterior: encoderStr(RES['360p'],  10),
    encoderInterior: encoderStr(RES['360p'],  10),
    sourceExterior:  sourceStr(RES['720p'],  20),
    sourceInterior:  sourceStr(RES['720p'],  20),
  },
];

/**
 * Default bitrate (Kbps) per camera + resolution, from the VAN-1377 tables.
 * Used to convert legacy STATIC_QP encoder strings to a BITRATE for display.
 */
const BITRATE_DEFAULTS_KBPS = {
  roadFacing: { 0: 900, 1: 4000, 2: 6500 },
  inCab: { 0: 500, 1: 2000, 2: 4000 },
} as const;

export interface VideoProfileTargetNames {
  encoderExterior: string;
  encoderInterior: string;
  sourceExterior: string;
  sourceInterior: string;
}

/**
 * Expand a chosen profile (or custom values) into the four REAL settings that
 * are sent to the device. This is the only thing the modal submits.
 *
 * `names` is the resolved registered Name for each target. Callers pass the
 * Names resolved from the fetched SettingTypes (label -> Name), since the
 * device keys on Name, which may differ from the displayed Label. Defaults to
 * the human identifiers for legacy settings where Name == Label.
 */
export function expandToSettings(
  values: VideoProfileValues,
  names: VideoProfileTargetNames = VIDEO_PROFILE_TARGET_SETTINGS,
): Setting[] {
  return [
    {
      name: names.encoderExterior,
      value: values.encoderExterior,
      displayValue: values.encoderExterior,
    },
    {
      name: names.encoderInterior,
      value: values.encoderInterior,
      displayValue: values.encoderInterior,
    },
    {
      name: names.sourceExterior,
      value: values.sourceExterior,
      displayValue: values.sourceExterior,
    },
    {
      name: names.sourceInterior,
      value: values.sourceInterior,
      displayValue: values.sourceInterior,
    },
  ];
}

/**
 * Reverse-match four current values back to a known profile for display
 * (e.g. the grouped grid row). Returns null when nothing matches => "Custom".
 */
export function matchProfile(values: VideoProfileValues): VideoProfile | null {
  return (
    VIDEO_PROFILES.find(
      (p) =>
        p.encoderExterior === values.encoderExterior &&
        p.encoderInterior === values.encoderInterior &&
        p.sourceExterior === values.sourceExterior &&
        p.sourceInterior === values.sourceInterior,
    ) ?? null
  );
}

/** Badge label for the grouped grid row: a known profile name, or "Custom". */
export function profileBadgeLabel(values: VideoProfileValues): string {
  return matchProfile(values)?.label ?? 'Custom';
}

/**
 * Pull the four target setting values out of a name->value map (e.g. the rows
 * the API returned for an entity). Returns null when none of the four settings
 * are present, so the grouped row is only shown when relevant.
 */
export function extractProfileValues(
  valueByName: Map<string, string>,
): VideoProfileValues | null {
  const encoderExterior = valueByName.get(VIDEO_PROFILE_TARGET_SETTINGS.encoderExterior);
  const encoderInterior = valueByName.get(VIDEO_PROFILE_TARGET_SETTINGS.encoderInterior);
  const sourceExterior = valueByName.get(VIDEO_PROFILE_TARGET_SETTINGS.sourceExterior);
  const sourceInterior = valueByName.get(VIDEO_PROFILE_TARGET_SETTINGS.sourceInterior);

  if (
    encoderExterior === undefined &&
    encoderInterior === undefined &&
    sourceExterior === undefined &&
    sourceInterior === undefined
  ) {
    return null;
  }

  return {
    encoderExterior: encoderExterior ?? 'NA',
    encoderInterior: encoderInterior ?? 'NA',
    sourceExterior: sourceExterior ?? 'NA',
    sourceInterior: sourceInterior ?? 'NA',
  };
}

